import makeStyles from '@mui/styles/makeStyles';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { API } from '../../../shared/constants/constants';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import { useEffect, useState } from 'react';
import ArchiveIcon from '@mui/icons-material/Archive';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import MergeIcon from '@mui/icons-material/Merge';
import MergeOrgsModal from './MergeOrgsModal';
import {
  getOrganizationsByCaseInsensitiveName,
  getOrgsByCaseInsensitiveName,
  getPersonnelByOrg,
  getPersonnelByOrganization,
} from '../contactsRequest';
import { countActiveContacts } from '../utils';
import AlertCannotArchiveOrg from '../AlertConnotArchiveOrg';
import { useArchiveContact } from '../Hooks/useArchiveContact';

const ContactsPageEditOutletModal = (props) => {
  const { openModal, setOpenModal, setRefreshContacts, selectedOrg, category, customFieldConfigs } = props;
  const [textFieldValue, setTextFieldValue] = useState('');
  const [customFieldTextFieldValues, setCustomFieldTextFieldValues] = useState(
    customFieldConfigs.map((config) => ({
      fieldKey: config.fieldKey,
      label: config.label,
      value: selectedOrg[config.fieldKey] ? selectedOrg[config.fieldKey] : '',
    }))
  );
  const [openMergeOrg, setOpenMergeOrg] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  const dispatch = useDispatch();
  const useStyles = makeStyles(() => ({
    dialog: {
      minHeight: '15vh',
      overflow: 'visible !important',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
    },
  }));
  const classes = useStyles();

  const archiveContactMutation = useArchiveContact({
    contactId: selectedOrg?._id,
    category: category,
    isArchiving: true,
    setRefetchContacts: setRefreshContacts,
  });

  useEffect(() => {
    setTextFieldValue(selectedOrg.name);
    setCustomFieldTextFieldValues(
      customFieldConfigs.map((config) => ({
        fieldKey: config.fieldKey,
        label: config.label,
        value: selectedOrg[config.fieldKey] ? selectedOrg[config.fieldKey] : '',
      }))
    );
  }, [selectedOrg, customFieldConfigs]);

  const handleClose = () => {
    setOpenModal(false);
    setCustomFieldTextFieldValues(
      customFieldConfigs.map((config) => ({
        fieldKey: config.fieldKey,
        label: config.label,
        value: selectedOrg[config.fieldKey] ? selectedOrg[config.fieldKey] : '',
      }))
    );
  };

  const countDuplicatesButExcludeSelected = async (outletName) => {
    try {
      let response;
      if (category === 'Outlets') {
        response = await getOrgsByCaseInsensitiveName(outletName);
      } else if (category === 'Organizations') {
        response = await getOrganizationsByCaseInsensitiveName(outletName);
      }
      response.data = response.data.filter((outlet) => {
        return outlet._id !== selectedOrg._id;
      });
      return response.data.length;
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const countActiveContactsOfSelectedOrg = async () => {
    try {
      let response;
      if (category === 'Outlets') {
        response = await getPersonnelByOrg(selectedOrg._id);
      } else if (category === 'Organizations') {
        response = await getPersonnelByOrganization(selectedOrg._id);
      }
      return countActiveContacts(response.data);
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const handleArchive = async () => {
    const numActiveContactOfOrg = await countActiveContactsOfSelectedOrg();
    if (numActiveContactOfOrg === 0) {
      await archiveContactMutation.mutateAsync();
    } else {
      setOpenAlert(true);
    }
  };

  const handleEdit = async (event) => {
    event.preventDefault();
    const duplicates = await countDuplicatesButExcludeSelected(textFieldValue);

    if (duplicates > 0) {
      dispatch(
        openSnackbar({
          severity: 'warning',
          message: `${duplicates} outlet(s) with the same name already exists. Please check if you are trying to update an outlet to one that already exists.`,
        })
      );
      return;
    } else {
      try {
        let response;
        const updateData = { name: textFieldValue };
        customFieldTextFieldValues.forEach((c) => {
          updateData[c.fieldKey] = c.value;
        });
        if (category === 'Outlets') {
          response = await API.put(`/org/${selectedOrg._id}`, updateData);
        } else {
          response = await API.put(`/organization/${selectedOrg._id}`, updateData);
        }

        if (response.status !== 200) {
          alert(response.statusText);
        } else {
          dispatch(
            openSnackbar({
              severity: 'success',
              message: `${category.slice(0, -1)} updated!`,
            })
          );
        }

        setRefreshContacts(true);
        handleClose();
        event.stopPropagation();
      } catch (err) {
        alert(`post failed - ${err}`);
      }
    }
  };

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.dialog}>
      <form onSubmit={handleEdit}>
        <DialogTitle>Edit {category.slice(0, -1)}</DialogTitle>
        <DialogContent className={classes.dialog}>
          <TextField
            required
            autoFocus
            value={textFieldValue}
            onChange={(event) => {
              setTextFieldValue(event.target.value);
            }}
            margin="dense"
            id="name"
            label="Name"
            type="text"
            variant="standard"
            data-cy="name-input-ContactsPageEditOutletModal"
          />
          {customFieldTextFieldValues.map((customFieldTextFieldValue) => (
            <TextField
              key={customFieldTextFieldValue.fieldKey}
              value={customFieldTextFieldValue.value}
              id={customFieldTextFieldValue.fieldKey}
              label={customFieldTextFieldValue.label}
              data-cy={`contact-edit-outlet-modal-textfield-custom-field-${customFieldTextFieldValue.label}`}
              type="text"
              variant="standard"
              onChange={(e) => {
                setCustomFieldTextFieldValues(
                  customFieldTextFieldValues.map((c) => {
                    if (c.fieldKey === customFieldTextFieldValue.fieldKey) {
                      return {
                        ...c,
                        value: e.target.value,
                      };
                    }
                    return c;
                  })
                );
              }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-cy="cancel-button-ContactsPageEditOutletModal">
            Cancel
          </Button>
          <Button
            onClick={() => setOpenMergeOrg(true)}
            startIcon={<MergeIcon />}
            data-cy="merge-button-ContactsPageEditOutletModal"
          >
            Merge
          </Button>
          <Button
            onClick={() => handleArchive()}
            startIcon={<ArchiveIcon />}
            data-cy="archive-button-ContactsPageEditOutletModal"
          >
            Archive
          </Button>
          <Button type="submit" startIcon={<SyncAltIcon />} data-cy="update-button-ContactsPageEditOutletModal">
            Update
          </Button>
        </DialogActions>
      </form>

      <MergeOrgsModal
        openMergeOrg={openMergeOrg}
        setOpenMergeOrg={setOpenMergeOrg}
        selectedOrg={selectedOrg}
        setRefreshContacts={setRefreshContacts}
        setOpenEditOrg={setOpenModal}
        category={category}
      />

      <AlertCannotArchiveOrg open={openAlert} setOpen={setOpenAlert} />
    </Dialog>
  );
};

export default ContactsPageEditOutletModal;
